import { Suspense } from 'react';
import ScrollToTop from './helpers/scroll-top';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import {
  authSelector,
  logout,
  setShoppingCart,
} from './store/slices/auth-slice';
import routes from './routesConfig';
import ProtectedRoute from './components/ProtectedRoute';
import cogoToast from 'cogo-toast';
import { store } from './store/store';
import config from './utils/config';

// Axios default settings
axios.defaults.baseURL = `${config.apiBaseUrl}/rest`;
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.withCredentials = true;

const Preloader = () => (
  <div className="flone-preloader-wrapper">
    <div className="flone-preloader">
      <span></span>
      <span></span>
    </div>
  </div>
);

const App = () => {
  const dispatch = useDispatch();
  const { accessToken } = useSelector(authSelector);
  const isAuthenticated = !!accessToken;

  // Request Interceptor
  axios.interceptors.request.use(
    (config) => {
      const state = store.getState();
      const { accessToken, shoppingCart, client } = state.auth;

      if (accessToken && config.headers) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      if (['post', 'put', 'patch'].includes(config.method)) {
        config.data = config.data || {};

        if (
          config.url !== '/services/initializeSubstituteOrderCart' &&
          config.url !== '/services/initializeOrderEditCart'
        ) {
          // responseMap is declared mode="INOUT" on several services, which OFBiz
          // validates more strictly than optional="true" alone implies — always
          // send the key (even empty) so a missing shoppingCart token (e.g. right
          // after an order is confirmed and its cart discarded) doesn't turn into
          // a 400 from a request body with no responseMap at all.
          if (!config.data.responseMap) {
            config.data.responseMap =
              config.url === '/services/getClientProducts' ? { ...client } : {};
          } else {
            config.data.responseMap =
              config.url === '/services/getClientProducts'
                ? { ...client, ...config?.data?.responseMap }
                : { ...config?.data?.responseMap };
          }
          if (shoppingCart) {
            config.data.responseMap.shoppingCart = shoppingCart;
          }
        }
      }

      return config;
    },
    (error) => {
      console.error('Request Error:', error);
      return Promise.reject(error);
    }
  );

  // Response Interceptor
  axios.interceptors.response.use(
    (response) => {
      if (response?.data?.data?.responseMap?.shoppingCart) {
        // console.log(response?.data?.data?.responseMap?.shoppingCart);
        dispatch(
          setShoppingCart(response?.data?.data?.responseMap?.shoppingCart)
        );
      }
      return response;
    },
    (error) => {
      const res = error?.response?.data ?? null;
      if (error?.response?.status === 401) {
        dispatch(logout());
      } else {
        let errMsg = 'Internal server error';
        if (res?.errorDescription) {
          errMsg = res.errorDescription;
        } else if (res?.errors) {
          errMsg = res.errors;
        }
        cogoToast.error(errMsg, {
          position: 'top-right',
          hideAfter: 5,
        });
        return;
      }
    }
  );
  return (
    <Router>
      <ScrollToTop>
        <Suspense fallback={<Preloader />}>
          <Routes>
            {routes.map((route, index) => {
              // Redirect logged-in users away from the login page
              if (route.redirectIfAuthenticated && isAuthenticated) {
                return (
                  <Route
                    key={index}
                    path={route.path}
                    element={
                      <Navigate to={process.env.PUBLIC_URL + '/'} replace />
                    }
                  />
                );
              }

              // Wrap protected routes with ProtectedRoute
              if (route.protected) {
                return (
                  <Route key={index} element={<ProtectedRoute />}>
                    <Route path={route.path} element={route.element} />
                  </Route>
                );
              }

              // Render public routes normally
              return (
                <Route key={index} path={route.path} element={route.element} />
              );
            })}
          </Routes>
        </Suspense>
      </ScrollToTop>
    </Router>
  );
};

export default App;
